import {
  COMPARE_METRICS,
  CROP_SOIL_METRICS,
  LOCATION_SOIL_METRICS,
  SOIL_PROPERTY_METRICS,
  VALIDATION_DEFAULT_SCENARIO_COLORS,
  VALIDATION_SCENARIO_COLORS,
} from "consts";

import { ValidationRow } from "models";

import {
  AverageRow,
  DeltaRow,
  LocationDeltaRow,
  LocationRotationDeltaRow,
  LocationRotationRow,
  LocationRow,
} from "pages/admin/soilSight/utils/compareTypes.ts";

export function getScenarioColor(scenario: string, index: number): string {
  return VALIDATION_SCENARIO_COLORS[scenario] ?? VALIDATION_DEFAULT_SCENARIO_COLORS[index % VALIDATION_DEFAULT_SCENARIO_COLORS.length] ?? "#999";
}

export const soilDeltaColor = (delta: number | null): string => {
  if (delta === null || Math.abs(delta) < 0.0001) return "#6b7280";
  return "#d97706";
};

export const valueColor = (value: number | null, higherIsBetter: boolean): string => {
  if (value === null || Math.abs(value) < 0.001) return "#6b7280";
  const positive = value > 0;
  const good = higherIsBetter ? positive : !positive;
  return good ? "#16a34a" : "#dc2626";
};

export const formatDelta = (value: number | null): string => {
  if (value === null) return "—";
  const abs = Math.abs(value);
  const formatted = abs >= 1000 ? Math.round(value).toLocaleString() : value.toFixed(2);
  return (value > 0 ? "+" : "") + formatted;
};

export function computeDeltas(a: ValidationRow[], b: ValidationRow[]): DeltaRow[] {
  const indexA = new Map<string, ValidationRow>();
  for (const row of a) {
    const key = `${row.location}|${row.rotation}|${row.scenario}|${row.year}`;
    indexA.set(key, row);
  }

  type Acc = {
    sum: Record<string, number>;
    count: Record<string, number>;
    newSum: Record<string, number>;
    newCount: Record<string, number>;
    matchedYears: number;
  };
  const groups = new Map<string, Acc>();
  const rotationCropsByName = new Map<string, string>();

  for (const rowB of b) {
    if (typeof rowB.rotationCrops === "string") rotationCropsByName.set(rowB.rotation, rowB.rotationCrops);
    const groupKey = `${rowB.location}|${rowB.rotation}|${rowB.scenario}`;
    const rowKey = `${rowB.location}|${rowB.rotation}|${rowB.scenario}|${rowB.year}`;
    const rowA = indexA.get(rowKey);
    if (rowA) {
      if (!groups.has(groupKey)) groups.set(groupKey, { sum: {}, count: {}, newSum: {}, newCount: {}, matchedYears: 0 });
      const acc = groups.get(groupKey)!;
      acc.matchedYears += 1;

      for (const m of COMPARE_METRICS) {
        const valA = rowA[m.key];
        const valB = rowB[m.key];
        if (typeof valA === "number" && typeof valB === "number") {
          acc.sum[m.key] = (acc.sum[m.key] ?? 0) + (valB - valA);
          acc.count[m.key] = (acc.count[m.key] ?? 0) + 1;
        }
      }

      for (const m of SOIL_PROPERTY_METRICS) {
        const valA = rowA[m.key];
        const valB = rowB[m.key];
        if (typeof valA === "number" && typeof valB === "number") {
          acc.sum[m.key] = (acc.sum[m.key] ?? 0) + (valB - valA);
          acc.count[m.key] = (acc.count[m.key] ?? 0) + 1;
        } else if (valA == null && typeof valB === "number") {
          acc.newSum[m.key] = (acc.newSum[m.key] ?? 0) + valB;
          acc.newCount[m.key] = (acc.newCount[m.key] ?? 0) + 1;
        }
      }
    }
  }

  const results: DeltaRow[] = [];
  for (const [groupKey, acc] of groups) {
    const [location, rotation, scenario] = groupKey.split("|") as [string, string, string];
    const deltas: Record<string, number | null> = {};
    for (const m of COMPARE_METRICS) {
      const count = acc.count[m.key] ?? 0;
      deltas[m.key] = count > 0 ? (acc.sum[m.key] ?? 0) / count : null;
    }
    const soilDeltas: Record<string, number | null> = {};
    const soilNewValues: Record<string, number | null> = {};
    for (const m of SOIL_PROPERTY_METRICS) {
      const count = acc.count[m.key] ?? 0;
      soilDeltas[m.key] = count > 0 ? (acc.sum[m.key] ?? 0) / count : null;
      const newCnt = acc.newCount[m.key] ?? 0;
      soilNewValues[m.key] = newCnt > 0 ? (acc.newSum[m.key] ?? 0) / newCnt : null;
    }
    results.push({
      location,
      rotation,
      rotationCrops: rotationCropsByName.get(rotation) ?? "",
      scenario,
      deltas,
      soilDeltas,
      soilNewValues,
      matchedYears: acc.matchedYears,
    });
  }

  results.sort((a, b) => `${a.location}|${a.rotation}|${a.scenario}`.localeCompare(`${b.location}|${b.rotation}|${b.scenario}`));

  return results;
}

export function computeAverages(rows: ValidationRow[]): AverageRow[] {
  type Acc = { sum: Record<string, number>; count: Record<string, number>; matchedYears: number };
  const groups = new Map<string, Acc>();
  const rotationCropsByName = new Map<string, string>();

  for (const row of rows) {
    if (typeof row.rotationCrops === "string") rotationCropsByName.set(row.rotation, row.rotationCrops);
    const groupKey = `${row.location}|${row.rotation}|${row.scenario}`;
    if (!groups.has(groupKey)) groups.set(groupKey, { sum: {}, count: {}, matchedYears: 0 });
    const acc = groups.get(groupKey)!;
    acc.matchedYears += 1;

    for (const m of [...COMPARE_METRICS, ...SOIL_PROPERTY_METRICS]) {
      const val = row[m.key];
      if (typeof val === "number") {
        acc.sum[m.key] = (acc.sum[m.key] ?? 0) + val;
        acc.count[m.key] = (acc.count[m.key] ?? 0) + 1;
      }
    }
  }

  const results: AverageRow[] = [];
  for (const [groupKey, acc] of groups) {
    const [location, rotation, scenario] = groupKey.split("|") as [string, string, string];
    const values: Record<string, number | null> = {};
    for (const m of COMPARE_METRICS) {
      const count = acc.count[m.key] ?? 0;
      values[m.key] = count > 0 ? (acc.sum[m.key] ?? 0) / count : null;
    }
    const soilValues: Record<string, number | null> = {};
    for (const m of SOIL_PROPERTY_METRICS) {
      const count = acc.count[m.key] ?? 0;
      soilValues[m.key] = count > 0 ? (acc.sum[m.key] ?? 0) / count : null;
    }
    results.push({
      location,
      rotation,
      rotationCrops: rotationCropsByName.get(rotation) ?? "",
      scenario,
      values,
      soilValues,
      matchedYears: acc.matchedYears,
    });
  }

  results.sort((a, b) => `${a.location}|${a.rotation}|${a.scenario}`.localeCompare(`${b.location}|${b.rotation}|${b.scenario}`));

  return results;
}

export const formatValue = (value: number | null): string => {
  if (value === null) return "—";
  const abs = Math.abs(value);
  return abs >= 1000 ? Math.round(value).toLocaleString() : value.toFixed(2);
};

// LOCATION_SOIL_METRICS are a pure function of location — group by location only, ignoring rotation/scenario/year.
export function computeLocationAverages(rows: ValidationRow[]): LocationRow[] {
  type Acc = { sum: Record<string, number>; count: Record<string, number> };
  const groups = new Map<string, Acc>();

  for (const row of rows) {
    const { location } = row;
    if (!groups.has(location)) groups.set(location, { sum: {}, count: {} });
    const acc = groups.get(location)!;
    for (const m of LOCATION_SOIL_METRICS) {
      const val = row[m.key];
      if (typeof val === "number") {
        acc.sum[m.key] = (acc.sum[m.key] ?? 0) + val;
        acc.count[m.key] = (acc.count[m.key] ?? 0) + 1;
      }
    }
  }

  const results: LocationRow[] = [];
  for (const [location, acc] of groups) {
    const values: Record<string, number | null> = {};
    for (const m of LOCATION_SOIL_METRICS) {
      const count = acc.count[m.key] ?? 0;
      values[m.key] = count > 0 ? (acc.sum[m.key] ?? 0) / count : null;
    }
    results.push({ location, values });
  }

  results.sort((a, b) => a.location.localeCompare(b.location));
  return results;
}

// CROP_SOIL_METRICS vary by the rotation's first crop — group by location + rotation, ignoring scenario/year.
export function computeLocationRotationAverages(rows: ValidationRow[]): LocationRotationRow[] {
  type Acc = { sum: Record<string, number>; count: Record<string, number> };
  const groups = new Map<string, Acc>();
  const rotationCropsByName = new Map<string, string>();

  for (const row of rows) {
    if (typeof row.rotationCrops === "string") rotationCropsByName.set(row.rotation, row.rotationCrops);
    const groupKey = `${row.location}|${row.rotation}`;
    if (!groups.has(groupKey)) groups.set(groupKey, { sum: {}, count: {} });
    const acc = groups.get(groupKey)!;
    for (const m of CROP_SOIL_METRICS) {
      const val = row[m.key];
      if (typeof val === "number") {
        acc.sum[m.key] = (acc.sum[m.key] ?? 0) + val;
        acc.count[m.key] = (acc.count[m.key] ?? 0) + 1;
      }
    }
  }

  const results: LocationRotationRow[] = [];
  for (const [groupKey, acc] of groups) {
    const [location, rotation] = groupKey.split("|") as [string, string];
    const values: Record<string, number | null> = {};
    for (const m of CROP_SOIL_METRICS) {
      const count = acc.count[m.key] ?? 0;
      values[m.key] = count > 0 ? (acc.sum[m.key] ?? 0) / count : null;
    }
    results.push({ location, rotation, rotationCrops: rotationCropsByName.get(rotation) ?? "", values });
  }

  results.sort((a, b) => `${a.location}|${a.rotation}`.localeCompare(`${b.location}|${b.rotation}`));
  return results;
}

// Delta (B minus A) version of computeLocationAverages, for the compare page.
export function computeLocationDeltas(a: ValidationRow[], b: ValidationRow[]): LocationDeltaRow[] {
  const indexA = new Map<string, ValidationRow>();
  for (const row of a) indexA.set(row.location, row);

  type Acc = { sum: Record<string, number>; count: Record<string, number> };
  const groups = new Map<string, Acc>();

  for (const rowB of b) {
    const { location } = rowB;
    const rowA = indexA.get(location);
    if (rowA) {
      if (!groups.has(location)) groups.set(location, { sum: {}, count: {} });
      const acc = groups.get(location)!;
      for (const m of LOCATION_SOIL_METRICS) {
        const valA = rowA[m.key];
        const valB = rowB[m.key];
        if (typeof valA === "number" && typeof valB === "number") {
          acc.sum[m.key] = (acc.sum[m.key] ?? 0) + (valB - valA);
          acc.count[m.key] = (acc.count[m.key] ?? 0) + 1;
        }
      }
    }
  }

  const results: LocationDeltaRow[] = [];
  for (const [location, acc] of groups) {
    const deltas: Record<string, number | null> = {};
    for (const m of LOCATION_SOIL_METRICS) {
      const count = acc.count[m.key] ?? 0;
      deltas[m.key] = count > 0 ? (acc.sum[m.key] ?? 0) / count : null;
    }
    results.push({ location, deltas });
  }

  results.sort((a, b) => a.location.localeCompare(b.location));
  return results;
}

// Delta (B minus A) version of computeLocationRotationAverages, for the compare page.
export function computeLocationRotationDeltas(a: ValidationRow[], b: ValidationRow[]): LocationRotationDeltaRow[] {
  const indexA = new Map<string, ValidationRow>();
  for (const row of a) indexA.set(`${row.location}|${row.rotation}`, row);

  type Acc = { sum: Record<string, number>; count: Record<string, number>; newSum: Record<string, number>; newCount: Record<string, number> };
  const groups = new Map<string, Acc>();
  const rotationCropsByName = new Map<string, string>();

  for (const rowB of b) {
    if (typeof rowB.rotationCrops === "string") rotationCropsByName.set(rowB.rotation, rowB.rotationCrops);
    const groupKey = `${rowB.location}|${rowB.rotation}`;
    const rowA = indexA.get(groupKey);
    if (!groups.has(groupKey)) groups.set(groupKey, { sum: {}, count: {}, newSum: {}, newCount: {} });
    const acc = groups.get(groupKey)!;
    for (const m of CROP_SOIL_METRICS) {
      const valA = rowA?.[m.key];
      const valB = rowB[m.key];
      if (typeof valA === "number" && typeof valB === "number") {
        acc.sum[m.key] = (acc.sum[m.key] ?? 0) + (valB - valA);
        acc.count[m.key] = (acc.count[m.key] ?? 0) + 1;
      } else if (valA == null && typeof valB === "number") {
        acc.newSum[m.key] = (acc.newSum[m.key] ?? 0) + valB;
        acc.newCount[m.key] = (acc.newCount[m.key] ?? 0) + 1;
      }
    }
  }

  const results: LocationRotationDeltaRow[] = [];
  for (const [groupKey, acc] of groups) {
    const [location, rotation] = groupKey.split("|") as [string, string];
    const deltas: Record<string, number | null> = {};
    const newValues: Record<string, number | null> = {};
    for (const m of CROP_SOIL_METRICS) {
      const count = acc.count[m.key] ?? 0;
      deltas[m.key] = count > 0 ? (acc.sum[m.key] ?? 0) / count : null;
      const newCnt = acc.newCount[m.key] ?? 0;
      newValues[m.key] = newCnt > 0 ? (acc.newSum[m.key] ?? 0) / newCnt : null;
    }
    results.push({ location, rotation, rotationCrops: rotationCropsByName.get(rotation) ?? "", deltas, newValues });
  }

  results.sort((a, b) => `${a.location}|${a.rotation}`.localeCompare(`${b.location}|${b.rotation}`));
  return results;
}
